import CalculateDashboard from "./CalculateDashboard";
import "./calculate.css";
import { readHistory, createReview, resumeReview, pauseReview, beginReveal, recordCredits, checkpointReview, restoreReview, completeReview, storeCompleted } from "./reviewHistory";
import "./chamber.css";
import "./brand-tutorial.css";
import RevelateLogo, { RevelateDialogue } from "./RevelateLogo";
import VisualTutorial from "./VisualTutorial";
import { REVELATE_DICTIONARY } from "./revelate_hybrid_dictionary";
import { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { chooseContinueGame, readPersistentSavedGame, writePersistentSavedGame } from "./savedGame";

const COLS = 8;
const ROWS = 11;
const BASE_DROP = 1100;
const SOFT_DROP_INTERVAL = 90;
const HORIZONTAL_HOLD_DELAY = 240;
const HORIZONTAL_MOVE_INTERVAL = 105;
const HIGHLIGHT_MS = 500;
const LEVELUP_MS = 1500;
const VOWELS = new Set(["A", "E", "I", "O", "U"]);
const WILD = "?";
const WILD_INTERVAL = 12;

const WEIGHTED_LETTERS = [
  ...Array(16).fill("A"),
  ...Array(16).fill("E"),
  ...Array(10).fill("I"),
  ...Array(10).fill("O"),
  ...Array(5).fill("U"),
  ...Array(11).fill("R"),
  ...Array(11).fill("T"),
  ...Array(10).fill("N"),
  ...Array(10).fill("S"),
  ...Array(9).fill("L"),
  ...Array(8).fill("D"),
  ...Array(4).fill("G"),
  ...Array(4).fill("B"),
  ...Array(3).fill("C"),
  ...Array(4).fill("M"),
  ...Array(5).fill("P"),
  ...Array(2).fill("F"),
  ...Array(4).fill("H"),
  ...Array(2).fill("V"),
  ...Array(2).fill("W"),
  ...Array(2).fill("Y"),
  ...Array(1).fill("QU"),
  ...Array(2).fill("K"),
  ...Array(1).fill("J"),
  ...Array(1).fill("X"),
  ...Array(1).fill("Z"),
];

const recentLetters = [];
const DICTIONARY = REVELATE_DICTIONARY;
const MAX_WORD = 8;
const BONUS_LETTERS = new Set(["Q", "X", "Z"]);
const BUILD_BONUS_LETTERS = ["B", "C", "F", "G", "H", "J", "K", "V", "W", "Y"];
const LONG_WORD_TARGET = 3;


function getBuildBonusLetter(date = new Date()) {
  const utcDay = Math.floor(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()) /
      86400000
  );
  return BUILD_BONUS_LETTERS[utcDay % BUILD_BONUS_LETTERS.length];
}

function expandTile(tile) {
  return tile === WILD ? [WILD] : tile.split("");
}

function randomLetter(helpPool = []) {
  const recentWindow = recentLetters.slice(-8);

  const counts = {};
  for (const l of recentWindow) {
    counts[l] = (counts[l] || 0) + 1;
  }

  const last4 = recentLetters.slice(-4);
  const vowelCountLast4 = last4.filter((l) => VOWELS.has(l)).length;
  const forceVowel = last4.length === 4 && vowelCountLast4 === 0;

  let pool;

  if (forceVowel) {
    pool = WEIGHTED_LETTERS.filter((l) => l.length === 1 && VOWELS.has(l));
  } else if (helpPool.length > 0 && Math.random() < 0.24) {
    pool = [...helpPool];
  } else {
    pool = [...WEIGHTED_LETTERS];
  }

  let filtered = pool.filter((l) => {
    if (l === "QU" && recentWindow.includes("QU")) return false;
    if ((counts[l] || 0) >= 2) return false;
    if (
      recentWindow.length > 0 &&
      recentWindow[recentWindow.length - 1] === l
    ) {
      return false;
    }
    return true;
  });

  if (filtered.length === 0) {
    filtered = pool.filter((l) => !(l === "QU" && recentWindow.includes("QU")));
  }

  if (filtered.length === 0) {
    filtered = pool;
  }

  const letter = filtered[Math.floor(Math.random() * filtered.length)];

  recentLetters.push(letter);
  if (recentLetters.length > 8) recentLetters.shift();

  return letter;
}

function helpfulLetters(grid) {
  const boardCounts = {};

  for (const row of grid) {
    for (const cell of row) {
      if (!cell) continue;
      for (const ch of expandTile(cell)) {
        boardCounts[ch] = (boardCounts[ch] || 0) + 1;
      }
    }
  }

  const helpful = [];

  for (const word of DICTIONARY) {
    if (word.length > 6) continue;

    const needed = {};
    for (const l of word) needed[l] = (needed[l] || 0) + 1;

    let missingCount = 0;
    let missingLetter = null;

    for (const [l, cnt] of Object.entries(needed)) {
      const have = boardCounts[l] || 0;
      const short = cnt - have;
      if (short > 0) {
        missingCount += short;
        if (short === 1 && missingLetter === null) missingLetter = l;
      }
    }

    if (missingCount === 1 && missingLetter) {
      helpful.push(missingLetter === "Q" ? "QU" : missingLetter);
    }
  }

  return helpful;
}

function calcDropInterval(level, score) {
  const levelSpeed = (level - 1) * 20;
  const scoreSpeed = Math.floor(score / 500) * 20;

  return Math.max(220, BASE_DROP - levelSpeed - scoreSpeed);
}

function wordPoints(word) {
  const len = word.length;
  let pts =
    len === 3 ? 30 : len === 4 ? 60 : len === 5 ? 100 : len === 6 ? 150 : 200;
  if ([...word].some((l) => BONUS_LETTERS.has(l))) pts *= 2;
  return pts;
}

function chainMult(depth) {
  return depth === 0 ? 1 : depth === 1 ? 1.5 : 2;
}

function createEmptyGrid() {
  return Array.from({ length: ROWS }, () => Array(COLS).fill(null));
}

function newBlock(letter) {
  return { row: 0, col: 3, letter: letter ?? randomLetter() };
}

function isOccupied(g, row, col) {
  if (row < 0 || row >= ROWS || col < 0 || col >= COLS) return true;
  return g[row][col] !== null;
}

function key(r, c) {
  return `${r},${c}`;
}

function isBoardEmpty(g) {
  return g.every((row) => row.every((c) => c === null));
}

function resolveDictionaryWord(letters) {
  const normalized = Array.isArray(letters) ? letters : String(letters || "").split("");
  const cleanWord = normalized.join("").toLowerCase();

  if (!normalized.includes(WILD)) {
    return DICTIONARY.has(cleanWord) ? cleanWord.toUpperCase() : null;
  }

  const tryLetters = [...normalized];
  function resolveNext() {
    const idx = tryLetters.indexOf(WILD);
    if (idx < 0) {
      const candidate = tryLetters.join("").toLowerCase();
      return DICTIONARY.has(candidate) ? candidate.toUpperCase() : null;
    }

    for (let code = 65; code <= 90; code++) {
      tryLetters[idx] = String.fromCharCode(code);
      const resolved = resolveNext();
      if (resolved) return resolved;
    }
    tryLetters[idx] = WILD;
    return null;
  }

  return resolveNext();
}

function dictMatch(letters) {
  return resolveDictionaryWord(letters) !== null;
}

function findWordsInSequence(cells) {
  const matchedKeys = new Set();
  const matchedWords = [];
  let i = 0;

  while (i < cells.length) {
    let found = false;

    for (let end = cells.length; end > i; end--) {
      const segment = cells.slice(i, end);
      const letters = segment.map((x) => x.letter);
      const joined = letters.join("");

      if (joined.length < 3 || joined.length > MAX_WORD) continue;

      if (dictMatch(letters)) {
        for (let k = 0; k < segment.length; k++) {
          matchedKeys.add(key(segment[k].r, segment[k].c));
        }
        matchedWords.push(joined);
        i = end;
        found = true;
        break;
      }
    }

    if (!found) i++;
  }

  return { matchedKeys, matchedWords };
}


function removeAndGravity(grid, toRemove) {
  const newGrid = Array.from({ length: ROWS }, () => Array(COLS).fill(null));

  for (let col = 0; col < COLS; col++) {
    const survivors = [];

    for (let row = ROWS - 1; row >= 0; row--) {
      const cell = grid[row][col];
      const shouldRemove = toRemove.has(key(row, col));

      if (cell && !shouldRemove) {
        survivors.push(cell);
      }
    }

    let writeRow = ROWS - 1;

    for (const cell of survivors) {
      newGrid[writeRow][col] = cell;
      writeRow--;
    }
  }

  return newGrid;
}

function getViewportSize() {
  return {
    width: Math.floor(window.visualViewport?.width || window.innerWidth),
    height: Math.floor(window.visualViewport?.height || window.innerHeight),
  };
}

export default function App() {
  const buildBonusLetter = getBuildBonusLetter();
  const [viewport, setViewport] = useState(getViewportSize);
  const [screen, setScreen] = useState("menu");
  const surfaceRef = useRef(null);
  useEffect(() => {
    // Home and tutorial have independent reading positions on small screens.
    if (surfaceRef.current) surfaceRef.current.scrollTop = 0;
  }, [screen]);
  const [grid, setGrid] = useState(createEmptyGrid);
  const [block, setBlock] = useState(newBlock);
  const [gameOver, setGameOver] = useState(false);
  const [highlighted, setHighlighted] = useState(new Set());
  const [invalidHighlight, setInvalidHighlight] = useState(new Set());
  const [clearing, setClearing] = useState(false);
  const [softDropHeld, setSoftDropHeld] = useState(false);
  const [wheelSoftDropActive, setWheelSoftDropActive] = useState(false);
  const [horizontalMoveHeld, setHorizontalMoveHeld] = useState(null);
  const [score, setScore] = useState(0);
  const [bestScore, setBestScore] = useState(() => {
    return Number(localStorage.getItem("revelate_best") || 0);
  });
  const [bestLongestWord, setBestLongestWord] = useState(() => {
    return localStorage.getItem("revelate_longest_word") || "";
  });
  const [showBuildBonusHit, setShowBuildBonusHit] = useState(false);
  const [level, setLevel] = useState(1);
  const [longWords, setLongWords] = useState(0);
  const [scorePops, setScorePops] = useState([]);
  const [selectionReveal, setSelectionReveal] = useState(null);
  const [wordReceipt, setWordReceipt] = useState(null);
  const [lastLongWordMilestone, setLastLongWordMilestone] = useState(0);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [nextLetter, setNextLetter] = useState(randomLetter);

  const [selectedCells, setSelectedCells] = useState(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [dragPath, setDragPath] = useState([]);
  const [savedGame, setSavedGame] = useState(null);
  const [persistentSavedGame, setPersistentSavedGame] = useState(() => readPersistentSavedGame());
  const [saveConfirmed, setSaveConfirmed] = useState(false);
  const reviewRef = useRef(null);
  const reviewFinishedRef = useRef(false);
  const [reviewGames, setReviewGames] = useState(readHistory);
  const [reviewGame, setReviewGame] = useState(null);
  const [reviewAnchor, setReviewAnchor] = useState(null);
  const [reviewLanding, setReviewLanding] = useState(false);
  function captureReviewAnchor() {
    const surface = surfaceRef.current;
    const logo = surface?.querySelector('.menu-surface .logo-home');
    const header = surface?.querySelector('.masthead');
    if (!logo || !header) return;
    const rect = logo.getBoundingClientRect();
    setReviewAnchor({left:rect.left,paddingTop:Math.max(0,rect.top-header.getBoundingClientRect().bottom-24),width:viewport.width,height:viewport.height});
  }
  useLayoutEffect(() => {
    if (!reviewLanding || screen !== "menu") return;
    captureReviewAnchor();
    setReviewLanding(false);
    setScreen("calculate");
  }, [reviewLanding, screen]);
  const [reviewStorageWarning, setReviewStorageWarning] = useState(false);

  useEffect(() => {
    if (!gameOver || screen !== "game" || reviewFinishedRef.current) return;
    reviewFinishedRef.current = true;
    const session = reviewRef.current || createReview(score);
    const finished = completeReview(session, {score,level,grid,block});
    const result = storeCompleted(finished);
    setReviewGames(result.games);
    setReviewStorageWarning(!result.persisted);
    setReviewGame(finished);
    setReviewLanding(true);
    setScreen("menu");
  }, [gameOver, screen, score, level, grid, block]);

  function openCalculate() {
    captureReviewAnchor();
    const games = readHistory();
    setReviewGames(games);
    setReviewGame(games[games.length - 1] || null);
    setScreen("calculate");
  }
  function reviewGroup(trigger) {
    if (!reviewRef.current) reviewRef.current = createReview(scoreRef.current);
    return beginReveal(reviewRef.current, trigger);
  }

const FULL_TIPS_TEXT = [
  {
    title: "OBJECTIVE",
    body: [
      "Build and clear words to score points and keep the board from filling up.",
      "Words can be formed across or down.",
      "Longer words score more and may include sub-words.",
      "Clear the board or make enough long words to level up.",
    ],
  },
  {
    title: "CONTROLS",
    body: [
      "TOUCHSCREEN",
      "Tap LEFT or RIGHT arrows to move one column.",
      "Tap and hold LEFT or RIGHT arrows to move continuously.",
      "Tap UP to attempt a hook.",
      "Tap or hold DOWN for a faster drop.",
      "Swipe across tiles to select, reveal, and score a word.",
      "",
      "COMPUTER",
      "A or Left Arrow: Move left.",
      "D or Right Arrow: Move right.",
      "W or Up Arrow: Attempt a hook.",
      "S or Down Arrow: Soft drop.",
      "Scroll up: Hook. Scroll down: Soft drop.",
      "Click and drag across tiles to select, reveal, and score a word.",
    ],
  },
  {
    title: "HOW WORDS WORK",
    body: [
      "Words must be 3 letters or longer.",
      "Words must exist in the game’s dictionary.",
      "Words can be formed horizontally or vertically.",
      "When you select a valid word, it highlights gold, clears, and tiles fall due to gravity.",
    ],
  },
  {
    title: "HOOK MECHANIC",
    body: [
      "Press UP when your falling tile lines up beside a row of tiles.",
      "The tile can snap into the end of a row or into a gap within the row.",
      "The placement must create a valid word of 3 or more letters.",
      "If it creates a valid word, the tile snaps into place and the word clears instantly.",
      "If it does not create a valid word, the tile continues falling.",
    ],
  },
  {
    title: "CASCADES",
    body: [
      "When words clear, tiles fall down.",
      "New words may form automatically.",
      "Swipe to highlight and clear any newly formed words.",
    ],
  },
  {
    title: "LONG WORDS",
    body: [
      "Words with 6 or more letters are critical.",
      "Progress is tracked at the top of the screen.",
      "Every third 6+ letter word advances you toward leveling up.",
    ],
  },
  {
    title: "LEVELING UP",
    body: [
      "You level up by making enough long words, completely clearing the board, and increasing your score.",
      "Each level increases game speed and difficulty.",
    ],
  },
  {
    title: "WILD TILE (?)",
    body: [
      "A wild tile can represent any letter.",
      "It helps complete difficult words.",
      "Only 1 wild tile can be used per valid word though",
      "Example: BAR?ER could be BARTER or BARKER.",
    ],
  },
  {
    title: "SCORING",
    body: [
      "Longer words score more points.",
      "Bonus letters (QU, X, Z) increase value.",
      "Words can score from multiple combinations.",
      "Base score by word length:",
      "3 letters: 50",
      "4 letters: 100",
      "5 letters: 175",
      "6 letters: 300",
      "7 letters: 450",
      "8 letters: 650",
      "9 letters: 900",
      "10+ letters: 1200+",
      "Example: BARTER scores as 6-letter word: 300, plus BAR: 50, plus ART: 50, for a total of 400.",
      "Big words create big score jumps.",
    ],
  },
  {
    title: "INVALID WORDS",
    body: [
      "If a word is not valid, it flashes red and no tiles are removed.",
    ],
  },
  {
    title: "STRATEGY TIPS",
    body: [
      "Build toward long words, not just quick clears.",
      "Look for words that contain smaller sub-words.",
      "Use the hook to clear words in tight or mid-stack spaces.",
      "Watch for new words created by cascading tiles.",
      "Do not rely only on horizontal plays. Vertical plays are essential.",
      "Use wild tiles to unlock big plays or substitute for rare letters.",
    ],
  },
{
  title: "FINAL TIP",
  body: [
    "Most players improve quickly after a few rounds.",
    "",
    "",
  ],
},
];

  // Size the entire 8 × 11 board from its available space, never crop rows.
  const [playSpace, setPlaySpace] = useState(null);
  const stageRef = useRef(null);
  const boardGap = viewport.height <= 550 || (viewport.width <= 550 && viewport.height <= 680) ? 2 : 4;
  const boardInset = viewport.height <= 550 && viewport.width > 550 ? 4 : 16;
  const cellSize = Math.max(14, Math.floor(Math.min(
    ((playSpace?.width || Math.min(viewport.width - 24, 520)) - boardInset - 7 * boardGap) / COLS,
    ((playSpace?.height || viewport.height - 236) - boardInset - 10 * boardGap) / ROWS
  )));
  const boardWidth = COLS * cellSize + (COLS - 1) * boardGap;
  const boardHeight = ROWS * cellSize + (ROWS - 1) * boardGap;
  const tileFontSize = Math.max(14, Math.round(cellSize * 0.61));
  const multiTileFontSize = Math.max(12, Math.round(cellSize * 0.43));
  const [hookImpact, setHookImpact] = useState(null);
  const [keyboardMode, setKeyboardMode] = useState(false);
  const [keyboardCursor, setKeyboardCursor] = useState({ row: 10, col: 0 });
  const keyboardModeRef = useRef(false);
  const keyboardCursorRef = useRef({ row: 10, col: 0 });
  const keyboardAnchorRef = useRef(null);

  useEffect(() => {
    if (screen !== "game" || !stageRef.current || !window.ResizeObserver) return;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setPlaySpace(previous => previous?.width === width && previous?.height === height ? previous : { width, height });
    });
    observer.observe(stageRef.current);
    return () => observer.disconnect();
  }, [screen]);

  useEffect(() => {
    if (!hookImpact) return;
    const timer = setTimeout(() => setHookImpact(null), 650);
    return () => clearTimeout(timer);
  }, [hookImpact]);

  const gridRef = useRef(grid);
  const blockRef = useRef(block);
  const gameOverRef = useRef(gameOver);
  const clearingRef = useRef(false);
  const scoreRef = useRef(0);
  const levelRef = useRef(1);
  const longWordsRef = useRef(0);
  const scorePopIdRef = useRef(0);
  const wordReceiptIdRef = useRef(0);
  const lastLongWordMilestoneRef = useRef(0);
  const nextLetterRef = useRef(nextLetter);
  const spawnCountRef = useRef(0);
  const boardTouchRef = useRef(null);
  const dragPathRef = useRef([]);
  const wheelSoftDropTimeoutRef = useRef(null);
  const buildBonusNoticeTimeoutRef = useRef(null);
  const saveConfirmedTimeoutRef = useRef(null);

  useEffect(() => () => {
    if (saveConfirmedTimeoutRef.current) clearTimeout(saveConfirmedTimeoutRef.current);
  }, []);

  // Presentation only: the existing clear timer and scoring remain authoritative.
  useEffect(() => {
    if (!wordReceipt) return;
    const timeout = setTimeout(() => setWordReceipt(null), 1400);
    return () => clearTimeout(timeout);
  }, [wordReceipt]);

  useEffect(() => {
    if (screen !== "game") {
      setSelectionReveal(null);
      setWordReceipt(null);
    }
  }, [screen]);

  const SCORE_BY_LENGTH = {
    3: 50,
    4: 100,
    5: 175,
    6: 300,
    7: 450,
    8: 650,
    9: 900,
    10: 1200,
  };

  function getWordScore(word) {
    const len = (word || "").length;
    if (len < 3) return 0;
    const basePoints = SCORE_BY_LENGTH[len] || 1200 + (len - 10) * 200;
    return word.includes(buildBonusLetter) ? basePoints * 2 : basePoints;
  }

  function getValidSubwords(word) {
    const cleanWord = String(word || "").toUpperCase().trim();
    const found = new Set();

    for (let start = 0; start < cleanWord.length; start++) {
      for (let end = start + 3; end <= cleanWord.length; end++) {
        const sub = cleanWord.slice(start, end);
        const letters = sub.split("");

        if (dictMatch(letters)) {
          found.add(sub);
        }
      }
    }

    return Array.from(found);
  }

  function getScoredWords(word) {
    const cleanWord = String(word || "").toUpperCase().trim();
    if (!cleanWord) return [];

    const hasWild = cleanWord.includes("?");
    const scored = [];

    if (hasWild) {
      // Resolve the wildcard once for the accepted full word, then score every
      // contiguous subword from that same spelling. This keeps one coherent
      // interpretation and prevents valid spans through ? from being skipped.
      const resolvedWord = resolveDictionaryWord(cleanWord);
      if (!resolvedWord) return [];
      scored.push(...getValidSubwords(resolvedWord));
    } else {
      const subwords = getValidSubwords(cleanWord);
      scored.push(...subwords);

      if (
        !scored.includes(cleanWord) &&
        DICTIONARY.has(cleanWord.toLowerCase())
      ) {
        scored.push(cleanWord);
      }
    }

    scored.sort((a, b) => b.length - a.length || a.localeCompare(b));
    return scored;
  }

  function getTotalWordScore(word) {
    const scoredWords = getScoredWords(word);
    return scoredWords.reduce((sum, w) => sum + getWordScore(w), 0);
  }

  const awardWordCredit = useCallback((word, reviewContext) => {
    const normalized =
      Array.isArray(word) ? word.join("") : String(word || "");

    const cleanWord = normalized.toUpperCase().trim();
    if (!cleanWord) return 0;

    const points = getTotalWordScore(cleanWord);
    if (!points) return 0;

    if (reviewContext) {
      recordCredits(reviewContext.group, cleanWord,
        getScoredWords(cleanWord).map(scoredWord => ({word:scoredWord, points:getWordScore(scoredWord)})),
        reviewContext, buildBonusLetter);
    }

    if (cleanWord.includes(buildBonusLetter)) {
      if (buildBonusNoticeTimeoutRef.current) {
        clearTimeout(buildBonusNoticeTimeoutRef.current);
      }
      setShowBuildBonusHit(true);
      buildBonusNoticeTimeoutRef.current = setTimeout(() => {
        setShowBuildBonusHit(false);
        buildBonusNoticeTimeoutRef.current = null;
      }, 900);
    }

    if (!cleanWord.includes(WILD)) {
      setBestLongestWord((previous) => {
        if (cleanWord.length <= previous.length) return previous;
        localStorage.setItem("revelate_longest_word", cleanWord);
        return cleanWord;
      });
    }

    setScore((prev) => {
      const next = prev + points;
      scoreRef.current = next;
      return next;
    });

    if (cleanWord.length >= 6) {
      setLongWords((prev) => {
        const next = prev + 1;
        longWordsRef.current = next;
        return next;
      });
    }

    return points;
  }, [buildBonusLetter]);

  const addScorePop = useCallback((points, row, col) => {
    const id = scorePopIdRef.current++;

    let size = 1;
    let duration = 1100;

    if (points >= 1200) {
      size = 2.2;
      duration = 1600;
    } else if (points >= 600) {
      size = 1.8;
      duration = 1400;
    } else if (points >= 300) {
      size = 1.4;
      duration = 1200;
    } else if (points >= 150) {
      size = 1.2;
    }

    setScorePops((prev) => [...prev, { id, points, row, col, size, duration }]);

    setTimeout(() => {
      setScorePops((prev) => prev.filter((p) => p.id !== id));
    }, duration);
  }, []);

  useEffect(() => {
    const updateViewport = () => {
      const nextViewport = getViewportSize();
      setViewport((current) =>
        current.width === nextViewport.width && current.height === nextViewport.height
          ? current
          : nextViewport
      );
    };

    updateViewport();

    window.addEventListener("resize", updateViewport);
    window.visualViewport?.addEventListener("resize", updateViewport);

    return () => {
      window.removeEventListener("resize", updateViewport);
      window.visualViewport?.removeEventListener("resize", updateViewport);
    };
  }, []);

  useEffect(() => {
    gridRef.current = grid;
  }, [grid]);

  useEffect(() => {
    blockRef.current = block;
  }, [block]);

  useEffect(() => {
    gameOverRef.current = gameOver;
  }, [gameOver]);

  useEffect(() => {
    clearingRef.current = clearing;
  }, [clearing]);

  useEffect(() => {
    scoreRef.current = score;
  }, [score]);

  useEffect(() => {
    levelRef.current = level;
  }, [level]);

  useEffect(() => {
    longWordsRef.current = longWords;
  }, [longWords]);

  useEffect(() => {
    lastLongWordMilestoneRef.current = lastLongWordMilestone;
  }, [lastLongWordMilestone]);

  useEffect(() => {
    nextLetterRef.current = nextLetter;
  }, [nextLetter]);



  function spawnNext(g) {
    spawnCountRef.current += 1;
    const next = newBlock(nextLetterRef.current);
    const helpPool = levelRef.current === 1 ? helpfulLetters(g) : [];
    const nextCount = spawnCountRef.current + 1;

    const fresh =
      levelRef.current === 1 && nextCount % WILD_INTERVAL === 0
        ? WILD
        : randomLetter(helpPool);

    setNextLetter(fresh);
    nextLetterRef.current = fresh;

    if (isOccupied(g, next.row, next.col)) {
      setGrid(g);
      setGameOver(true);
      setClearing(false);
    } else {
      setGrid(g);
      setBlock(next);
      setClearing(false);
    }
  }

  function triggerLevelUp(nextGrid) {
    const nextLevel = levelRef.current + 1;
    setLevel(nextLevel);
    levelRef.current = nextLevel;
    setLongWords(0);
    longWordsRef.current = 0;
    setShowLevelUp(true);

    setTimeout(() => {
      setShowLevelUp(false);
      spawnNext(nextGrid);
    }, LEVELUP_MS);
  }

  function finishChain(g, runningLongWords) {
    const empty = isBoardEmpty(g);
    if (runningLongWords >= LONG_WORD_TARGET || empty) {
      triggerLevelUp(g);
    } else {
      setLongWords(runningLongWords);
      longWordsRef.current = runningLongWords;
      spawnNext(g);
    }
  }

  function updateBestScore(nextScore) {
    const best = Number(localStorage.getItem("revelate_best") || 0);

    if (nextScore > best) {
      localStorage.setItem("revelate_best", String(nextScore));
      setBestScore(nextScore);
    }
  }


  function lockAndClear(b, g) {
    const updated = g.map((row) => [...row]);
    updated[b.row][b.col] = b.letter;

    const helpPool = levelRef.current === 1 ? helpfulLetters(updated) : [];
    spawnCountRef.current += 1;

    const nextCount = spawnCountRef.current + 1;
    const freshNext =
      levelRef.current === 1 && nextCount % WILD_INTERVAL === 0
        ? WILD
        : randomLetter(helpPool);

    const nextBlock = newBlock(nextLetterRef.current);

    setGrid(updated);
    gridRef.current = updated;

    setNextLetter(freshNext);
    nextLetterRef.current = freshNext;

    if (isOccupied(updated, nextBlock.row, nextBlock.col)) {
      updateBestScore(scoreRef.current);
      setGameOver(true);
      gameOverRef.current = true;
    } else {
      setBlock(nextBlock);
      blockRef.current = nextBlock;
    }

    setClearing(false);
    clearingRef.current = false;
  }

  function findHookMatch(updatedGrid, row, col) {
    function contiguousLine(dr, dc) {
      let startRow = row;
      let startCol = col;

      while (
        startRow - dr >= 0 &&
        startRow - dr < ROWS &&
        startCol - dc >= 0 &&
        startCol - dc < COLS &&
        updatedGrid[startRow - dr][startCol - dc] !== null
      ) {
        startRow -= dr;
        startCol -= dc;
      }

      const cells = [];
      let r = startRow;
      let c = startCol;

      while (
        r >= 0 &&
        r < ROWS &&
        c >= 0 &&
        c < COLS &&
        updatedGrid[r][c] !== null
      ) {
        cells.push({ r, c, letter: updatedGrid[r][c] });
        r += dr;
        c += dc;
      }

      return cells;
    }

    function bestWordInLine(cells) {
      if (cells.length < 3) return null;

      const hookIndex = cells.findIndex((cell) => cell.r === row && cell.c === col);
      if (hookIndex === -1) return null;

      const validSpans = [];

      for (let start = 0; start < cells.length; start++) {
        for (let end = start + 1; end <= cells.length; end++) {
          const segment = cells.slice(start, end);
          const letters = segment.map((x) => x.letter);
          const joined = letters.join("");

          if (joined.length < 3 || joined.length > MAX_WORD) continue;
          if (!dictMatch(letters)) continue;

          validSpans.push({ start, end, segment, word: joined });
        }
      }

      const connectedSpans = validSpans.filter(
        (span) => span.start <= hookIndex && hookIndex < span.end
      );

      for (let i = 0; i < connectedSpans.length; i++) {
        const current = connectedSpans[i];

        for (const span of validSpans) {
          const alreadyConnected = connectedSpans.includes(span);
          const overlaps = span.start < current.end && current.start < span.end;

          if (!alreadyConnected && overlaps) {
            connectedSpans.push(span);
          }
        }
      }

      if (connectedSpans.length === 0) return null;

      const clearStart = Math.min(...connectedSpans.map((span) => span.start));
      const clearEnd = Math.max(...connectedSpans.map((span) => span.end));
      const clearSegment = cells.slice(clearStart, clearEnd);
      const clearWord = clearSegment.map((cell) => cell.letter).join("");

      return { segment: clearSegment, word: clearWord, scoredSpans: connectedSpans };
    }

    const matches = [
      bestWordInLine(contiguousLine(0, 1)),
      bestWordInLine(contiguousLine(1, 0)),
    ].filter(Boolean);

    if (matches.length === 0) return null;

    const clearKeys = new Set();
    const matchedKeys = new Set();
    const matchedWords = [];

    for (const match of matches) {
      matchedWords.push(match.word);
      for (const cell of match.segment) {
        clearKeys.add(key(cell.r, cell.c));
      }
      for (const span of match.scoredSpans) {
        for (const cell of span.segment) {
          matchedKeys.add(key(cell.r, cell.c));
        }
      }
    }

    return { clearKeys, matchedKeys, matchedWords, reviewMatches:matches.map(match => match.segment) };
  }

  function attemptHook(dir) {
    if (gameOverRef.current || clearingRef.current) return false;

    const b = blockRef.current;
    const g = gridRef.current;

    const nextRow = b.row + 1;
    const isResting = nextRow >= ROWS || isOccupied(g, nextRow, b.col);

    if (isResting) return false;

    const dc = dir === "left" ? -1 : 1;
    const sideCol = b.col + dc;

    if (sideCol < 0 || sideCol >= COLS) return false;

    const rowsToTry = [b.row];

    for (const testRow of rowsToTry) {
      if (!isOccupied(g, testRow, sideCol)) continue;
      if (g[testRow][b.col] !== null) continue;

      const updated = g.map((row) => [...row]);
      updated[testRow][b.col] = b.letter;

      const hookMatch = findHookMatch(updated, testRow, b.col);

      if (hookMatch && hookMatch.matchedWords.length > 0) {
        let hookPoints = 0;
        const group = reviewGroup("hook");
        hookMatch.matchedWords.forEach((word, index) => {
          const cells = hookMatch.reviewMatches[index];
          hookPoints += awardWordCredit(word, {group, cells,
            direction:cells[0].r === cells[cells.length - 1].r ? "LR" : "UD",
            hookCell:{row:testRow,col:b.col}});
        });
        // Feedback observes a successful hook; it never drives the game clock.
        setHookImpact({ row: testRow, col: b.col, direction: dir, id: wordReceiptIdRef.current++ });
        setWordReceipt({ id: wordReceiptIdRef.current++, kind: "hook", words: hookMatch.matchedWords.join(" · "), points: hookPoints });

const toRemove = hookMatch.clearKeys;
const clearedGrid = removeAndGravity(updated, toRemove);

setClearing(true);
clearingRef.current = true;

setBlock({ ...b, row: testRow });
setHighlighted(toRemove);

setTimeout(() => {
setHighlighted(new Set());
setGrid(clearedGrid);
gridRef.current = clearedGrid;

const nextLongWords = longWordsRef.current;
const boardEmpty = isBoardEmpty(clearedGrid);

if (nextLongWords >= LONG_WORD_TARGET || boardEmpty) {
  setClearing(false);
  clearingRef.current = false;
  triggerLevelUp(clearedGrid);
  return;
}

setClearing(false);
clearingRef.current = false;

  const helpPool =
    levelRef.current === 1 ? helpfulLetters(clearedGrid) : [];
  spawnCountRef.current += 1;

  const nextCount = spawnCountRef.current + 1;
  const freshNext =
    levelRef.current === 1 && nextCount % WILD_INTERVAL === 0
      ? WILD
      : randomLetter(helpPool);

  const nextBlock = newBlock(nextLetterRef.current);

  setNextLetter(freshNext);
  nextLetterRef.current = freshNext;

  if (isOccupied(clearedGrid, nextBlock.row, nextBlock.col)) {
    setGameOver(true);
    gameOverRef.current = true;
  } else {
    setBlock(nextBlock);
    blockRef.current = nextBlock;
  }
}, 180);

        return true;
      }
    }

    return false;
  }

  function handleDirectionalInput(dir) {
    if (gameOverRef.current || clearingRef.current) return;

    const b = blockRef.current;
    const g = gridRef.current;
    const dc = dir === "left" ? -1 : 1;
    const nextCol = b.col + dc;

    if (nextCol >= 0 && nextCol < COLS && !isOccupied(g, b.row, nextCol)) {
      setBlock({ ...b, col: nextCol });
    }
  }

  useEffect(() => {
    if (screen !== "game" || gameOver) return;

    const delay = softDropHeld || wheelSoftDropActive
      ? SOFT_DROP_INTERVAL
      : calcDropInterval(level, score);
    const id = setInterval(() => {
      dropOneRow();
    }, delay);

    return () => clearInterval(id);
  }, [screen, gameOver, level, score, softDropHeld, wheelSoftDropActive]);

  useEffect(() => {
    if (!horizontalMoveHeld || screen !== "game" || gameOver) return;

    let intervalId = null;
    const delayId = setTimeout(() => {
      intervalId = setInterval(() => {
        handleDirectionalInput(horizontalMoveHeld);
      }, HORIZONTAL_MOVE_INTERVAL);
    }, HORIZONTAL_HOLD_DELAY);

    return () => {
      clearTimeout(delayId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [horizontalMoveHeld, screen, gameOver]);

  useEffect(() => {
    if (screen !== "game" || gameOver || clearing) {
      setSoftDropHeld(false);
      stopWheelSoftDrop();
      setHorizontalMoveHeld(null);
    }
  }, [screen, gameOver, clearing]);

  useEffect(() => {
    if (screen !== "game" || gameOver) return;

    function handleKey(e) {
      if (gameOverRef.current || clearingRef.current) return;
      if (handleKeyboardSelection(e)) return;
      const inputKey = e.key.toLowerCase();

      if (e.key === "ArrowLeft" || inputKey === "a") {
        e.preventDefault();
        if (!e.repeat) startHorizontalMove("left");
      } else if (e.key === "ArrowRight" || inputKey === "d") {
        e.preventDefault();
        if (!e.repeat) startHorizontalMove("right");
      } else if (e.key === "ArrowUp" || inputKey === "w") {
        e.preventDefault();
        attemptHook("left") || attemptHook("right");
      } else if (e.key === "ArrowDown" || inputKey === "s") {
        e.preventDefault();
        if (!e.repeat) startSoftDrop();
      }
    }

    function handleKeyUp(e) {
      const inputKey = e.key.toLowerCase();
      if (e.key === "ArrowDown" || inputKey === "s") stopSoftDrop();
      if (e.key === "ArrowLeft" || inputKey === "a") stopHorizontalMove("left");
      if (e.key === "ArrowRight" || inputKey === "d") stopHorizontalMove("right");
    }

    window.addEventListener("keydown", handleKey);
    window.addEventListener("keyup", handleKeyUp);
    function handleWindowBlur() {
      stopSoftDrop();
      stopWheelSoftDrop();
      stopHorizontalMove();
      cancelDragSelection();
    }

    window.addEventListener("blur", handleWindowBlur);
    return () => {
      window.removeEventListener("keydown", handleKey);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleWindowBlur);
    };
  }, [screen, gameOver]);

  function handleLeft() {
    handleDirectionalInput("left");
  }

  function handleRight() {
    handleDirectionalInput("right");
  }

  function dropOneRow() {
    if (gameOverRef.current || clearingRef.current) return false;
    const b = blockRef.current;
    const g = gridRef.current;
    const nextRow = b.row + 1;
    if (nextRow >= ROWS || isOccupied(g, nextRow, b.col)) {
      lockAndClear(b, g);
    } else {
      setBlock({ ...b, row: nextRow });
    }
    return true;
  }

  function startSoftDrop() {
    if (!dropOneRow()) return;
    setSoftDropHeld(true);
  }

  function stopSoftDrop() {
    setSoftDropHeld(false);
  }

  function startWheelSoftDrop() {
    if (!dropOneRow()) return;
    if (wheelSoftDropTimeoutRef.current) {
      clearTimeout(wheelSoftDropTimeoutRef.current);
    }
    setWheelSoftDropActive(true);
    wheelSoftDropTimeoutRef.current = setTimeout(() => {
      setWheelSoftDropActive(false);
      wheelSoftDropTimeoutRef.current = null;
    }, 150);
  }

  function stopWheelSoftDrop() {
    if (wheelSoftDropTimeoutRef.current) {
      clearTimeout(wheelSoftDropTimeoutRef.current);
      wheelSoftDropTimeoutRef.current = null;
    }
    setWheelSoftDropActive(false);
  }

  function startHorizontalMove(direction) {
    handleDirectionalInput(direction);
    setHorizontalMoveHeld(direction);
  }

  function stopHorizontalMove(direction) {
    setHorizontalMoveHeld((current) =>
      !direction || current === direction ? null : current
    );
  }

  function handleHook() {
    attemptHook("left") || attemptHook("right");
  }

  function handleWheel(e) {
    if (screen !== "game" || gameOverRef.current || clearingRef.current) return;

    e.preventDefault();
    if (e.deltaY > 0) {
      startWheelSoftDrop();
    } else if (e.deltaY < 0) {
      handleHook();
    }
  }

  // Keyboard selection uses the exact same straight-path selection and reveal
  // functions as mouse/touch. Falling and scoring continue at their original rate.
  function handleKeyboardSelection(e) {
    const inputKey = e.key.toLowerCase();
    if (inputKey === "r" && !e.repeat) {
      e.preventDefault();
      keyboardModeRef.current = !keyboardModeRef.current;
      setKeyboardMode(keyboardModeRef.current);
      cancelDragSelection();
      if (keyboardModeRef.current) {
        stopHorizontalMove();
        stopSoftDrop();
        boardTouchRef.current?.focus({ preventScroll: true });
      }
      return true;
    }
    if (!keyboardModeRef.current) return false;
    if (e.key === "Escape") {
      e.preventDefault();
      keyboardModeRef.current = false;
      setKeyboardMode(false);
      cancelDragSelection();
      return true;
    }
    if (e.key === " " && !e.repeat) {
      e.preventDefault();
      keyboardAnchorRef.current = { ...keyboardCursorRef.current };
      dragPathRef.current = [{ ...keyboardCursorRef.current }];
      setDragPath(dragPathRef.current);
      setSelectedCells(pathToKeySet(dragPathRef.current));
      setIsDragging(true);
      return true;
    }
    if (e.key === "Enter" && !e.repeat) {
      e.preventDefault();
      handleTouchEnd();
      keyboardAnchorRef.current = null;
      keyboardModeRef.current = false;
      setKeyboardMode(false);
      return true;
    }
    const offsets = { ArrowLeft: [0,-1], ArrowRight: [0,1], ArrowUp: [-1,0], ArrowDown: [1,0] };
    if (offsets[e.key]) {
      e.preventDefault();
      const [dr, dc] = offsets[e.key];
      const cursor = keyboardCursorRef.current;
      const next = { row: Math.max(0, Math.min(ROWS - 1, cursor.row + dr)), col: Math.max(0, Math.min(COLS - 1, cursor.col + dc)) };
      keyboardCursorRef.current = next;
      setKeyboardCursor(next);
      if (keyboardAnchorRef.current) {
        const nextPath = buildStraightPath(keyboardAnchorRef.current, next);
        dragPathRef.current = nextPath;
        setDragPath(nextPath);
        setSelectedCells(pathToKeySet(nextPath));
      }
      return true;
    }
    return false;
  }

  function handleTouchStart(e) {
    if (screen !== "game" || gameOver || clearing) return;

    const touch = e.touches?.[0];
    if (!touch) return;

    const start = getCellFromTouch(touch, boardTouchRef.current, cellSize, boardGap);
    if (!start) return;

    dragPathRef.current = [start];
    setIsDragging(true);
    setDragPath([start]);
    setSelectedCells(new Set([key(start.row, start.col)]));
  }

  function handleTouchMove(e) {
    if (!isDragging) return;

    const touch = e.touches?.[0];
    if (!touch) return;

    const start = dragPath[0];
    const current = getCellFromTouch(touch, boardTouchRef.current, cellSize, boardGap);
    if (!start || !current) return;

    const nextPath = buildStraightPath(start, current);
    dragPathRef.current = nextPath;
    setDragPath(nextPath);
    setSelectedCells(pathToKeySet(nextPath));
  }

  function handleTouchEnd() {
    const g = gridRef.current;
    const activeBlock = blockRef.current;
    const path =
      dragPathRef.current.length > 0
        ? [...dragPathRef.current]
        : getOrderedPathFromSelection(selectedCells);

    setIsDragging(false);
    dragPathRef.current = [];

    if (!path || path.length < 3) {
      setDragPath([]);
      setSelectedCells(new Set());
      return;
    }

    const { letters, hasBlank } = getWordFromPath(path, g, activeBlock);
    const pathCells = path.map((cell, index) => ({
      r: cell.row,
      c: cell.col,
      letter: letters[index],
    }));
    let sequenceMatch = findWordsInSequence(pathCells);
    if (sequenceMatch.matchedKeys.size !== path.length) {
      const reverseMatch = findWordsInSequence([...pathCells].reverse());
      if (reverseMatch.matchedKeys.size === path.length) {
        sequenceMatch = reverseMatch;
      }
    }
    const activeKey = key(activeBlock.row, activeBlock.col);
    const clearsActiveBlock = sequenceMatch.matchedKeys.has(activeKey);

if (hasBlank || sequenceMatch.matchedKeys.size !== path.length) {
  const invalidSet = pathToKeySet(path);

  setInvalidHighlight(invalidSet);

  setTimeout(() => {
    setInvalidHighlight(new Set());
  }, 200);

  setDragPath([]);
  setSelectedCells(new Set());
  return;
}

let totalPoints = 0;
const group = reviewGroup("selection");
sequenceMatch.matchedWords.forEach((word) => {
  totalPoints += awardWordCredit(word, {group, direction:path[0].row === path[path.length - 1].row ? "LR" : "UD"});
});

setSelectionReveal(path.map((cell, index) => ({ ...cell, letter: letters[index] })));
setWordReceipt({
  id: wordReceiptIdRef.current++,
  words: sequenceMatch.matchedWords.join(" · "),
  points: totalPoints,
});

const toRemove = sequenceMatch.matchedKeys;
const sourceGrid = clearsActiveBlock ? g.map((row) => [...row]) : g;

if (clearsActiveBlock) {
  sourceGrid[activeBlock.row][activeBlock.col] = activeBlock.letter;
}

const clearedGrid = removeAndGravity(sourceGrid, toRemove);

setClearing(true);
clearingRef.current = true;

setHighlighted(toRemove);

setTimeout(() => {
  setSelectionReveal(null);
  setHighlighted(new Set());
  setGrid(clearedGrid);
  gridRef.current = clearedGrid;
  setDragPath([]);
  setSelectedCells(new Set());
  setIsDragging(false);

  const nextLongWords = longWordsRef.current;
  const boardEmpty = isBoardEmpty(clearedGrid);

  if (nextLongWords >= LONG_WORD_TARGET || boardEmpty) {
    triggerLevelUp(clearedGrid);
    setClearing(false);
    clearingRef.current = false;
    return;
  }

  setClearing(false);
  clearingRef.current = false;
  if (clearsActiveBlock) {
    finishChain(clearedGrid, nextLongWords);
  }
}, 180);
  }

  function cancelDragSelection() {
    keyboardAnchorRef.current = null;
    setIsDragging(false);
    dragPathRef.current = [];
    setDragPath([]);
    setSelectedCells(new Set());
  }

  function handleMouseDown(e) {
    if (e.button !== 0) return;
    e.preventDefault();
    handleTouchStart({ touches: [e] });
  }

  function handleMouseMove(e) {
    if (!isDragging) return;
    handleTouchMove({ touches: [e] });
  }

  function goHome() {
    pauseReview(reviewRef.current);
    stopSoftDrop();
    stopWheelSoftDrop();
    stopHorizontalMove();
    cancelDragSelection();
    setShowBuildBonusHit(false);
    if (screen === "game" && !gameOver && !clearing && !showLevelUp) {
      setSavedGame({
        grid,
        block,
        score,
        level,
        longWords,
        nextLetter,
        gameOver,
        highlighted: Array.from(highlighted),
        clearing,
        selectedCells: Array.from(selectedCells),
        dragPath,
        spawnCount: spawnCountRef.current,
        lastLongWordMilestone,
        recentLetters: [...recentLetters],
      });
    }

    keyboardModeRef.current = false;
    setKeyboardMode(false);
    setHookImpact(null);
    setScreen("menu");
  }

  function saveGame() {
    if (!savedGame) return;
    if (writePersistentSavedGame(savedGame)) {
      if (reviewRef.current && !checkpointReview(reviewRef.current, savedGame)) setReviewStorageWarning(true);
      setPersistentSavedGame(readPersistentSavedGame());
      setSaveConfirmed(true);
      if (saveConfirmedTimeoutRef.current) {
        clearTimeout(saveConfirmedTimeoutRef.current);
      }
      saveConfirmedTimeoutRef.current = setTimeout(() => {
        setSaveConfirmed(false);
        saveConfirmedTimeoutRef.current = null;
      }, 1500);
    }
  }

  function continueGame() {
    // The current paused session is always preferred to the disk save.
    const gameToContinue = chooseContinueGame(savedGame, persistentSavedGame);
    if (!gameToContinue) return;
    if (!savedGame || !reviewRef.current) reviewRef.current = restoreReview(gameToContinue);
    resumeReview(reviewRef.current);
    reviewFinishedRef.current = false;

    const {
      grid,
      block,
      score,
      level,
      longWords,
      nextLetter,
      gameOver,
      highlighted,
      clearing,
      selectedCells,
      dragPath,
      spawnCount,
      lastLongWordMilestone,
      recentLetters: savedRecentLetters = [],
    } = gameToContinue;

    setGrid(grid);
    gridRef.current = grid;

    setBlock(block);
    blockRef.current = block;

    setScore(score);
    scoreRef.current = score;

    setLevel(level);
    levelRef.current = level;

    setLongWords(longWords);
    longWordsRef.current = longWords;

    setNextLetter(nextLetter);
    nextLetterRef.current = nextLetter;

    setGameOver(gameOver);
    gameOverRef.current = gameOver;

    setHighlighted(new Set(highlighted));
    setClearing(clearing);
    clearingRef.current = clearing;

    setSelectedCells(new Set(selectedCells));
    setDragPath(dragPath);
    dragPathRef.current = dragPath || [];

    spawnCountRef.current = spawnCount;
    lastLongWordMilestoneRef.current = lastLongWordMilestone;
    setLastLongWordMilestone(lastLongWordMilestone);
    recentLetters.length = 0;
    recentLetters.push(...savedRecentLetters);

    // A restored save is now a live game. Keep the persistent slot intact,
    // but do not leave an extra paused-session copy competing with it.
    setSavedGame(null);

    setScreen("game");
  }

  function startGame() {
    reviewRef.current = createReview();
    resumeReview(reviewRef.current);
    reviewFinishedRef.current = false;
    setSelectionReveal(null);
    setWordReceipt(null);
    stopSoftDrop();
    stopWheelSoftDrop();
    stopHorizontalMove();
    cancelDragSelection();
    setShowBuildBonusHit(false);
    setSavedGame(null);
    recentLetters.length = 0;

    const freshNext = randomLetter();
    const freshBlock = newBlock(randomLetter());
    const freshGrid = createEmptyGrid();

    gridRef.current = freshGrid;
    blockRef.current = freshBlock;
    gameOverRef.current = false;
    clearingRef.current = false;
    scoreRef.current = 0;
    levelRef.current = 1;
    longWordsRef.current = 0;
    lastLongWordMilestoneRef.current = 0;
    nextLetterRef.current = freshNext;
    spawnCountRef.current = 0;
    dragPathRef.current = [];

    setGrid(freshGrid);
    setBlock(freshBlock);
    setGameOver(false);
    setHighlighted(new Set());
    setClearing(false);
    setScore(0);
    setLevel(1);
    setLongWords(0);
    setLastLongWordMilestone(0);
    setShowLevelUp(false);
    setNextLetter(freshNext);
    setSelectedCells(new Set());
    setDragPath([]);
    setIsDragging(false);
    setScreen("game");
  }

  function isActiveBlock(r, c) {
    return !gameOver && !clearing && r === block.row && c === block.col;
  }

  function getLandingGhost() {
    if (screen !== "game" || gameOver || clearing) return null;

    let landingRow = block.row;
    while (
      landingRow + 1 < ROWS &&
      !isOccupied(grid, landingRow + 1, block.col)
    ) {
      landingRow += 1;
    }

    return landingRow === block.row
      ? null
      : { row: landingRow, col: block.col, letter: block.letter };
  }

  function cellBg(active, lit, hasLetter, letter) {
    if (active && letter === WILD) return "#2a7a2a";
    if (active) return "#fff";
    if (lit) return "#f5c400";
    if (hasLetter && letter === WILD) return "#2a7a2a";
    if (hasLetter) return "#fff";
    return "#0c0c0c";
  }

  function cellFg(active, lit, hasLetter, letter) {
    if (active && letter === WILD) return "#fff";
    if (active || lit) return "#000";
    if (hasLetter && letter === WILD) return "#fff";
    if (hasLetter) return "#000";
    return "#444";
  }

  function getCellFromTouch(touch, gridEl, size, gap = 2) {
    if (!gridEl) return null;

    const rect = gridEl.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;

    if (x < 0 || y < 0 || x > rect.width || y > rect.height) return null;

    const step = size + gap;
    const col = Math.floor(x / step);
    const row = Math.floor(y / step);

    if (row < 0 || row >= ROWS || col < 0 || col >= COLS) return null;

    return { row, col };
  }

  function buildStraightPath(start, end) {
    if (!start || !end) return [];

    if (start.row === end.row) {
      const row = start.row;
      const step = start.col <= end.col ? 1 : -1;
      const length = Math.abs(end.col - start.col) + 1;
      return Array.from({ length }, (_, i) => ({
        row,
        col: start.col + i * step,
      }));
    }

    if (start.col === end.col) {
      const col = start.col;
      const step = start.row <= end.row ? 1 : -1;
      const length = Math.abs(end.row - start.row) + 1;
      return Array.from({ length }, (_, i) => ({
        row: start.row + i * step,
        col,
      }));
    }

    return [];
  }

  function pathToKeySet(path) {
    return new Set(path.map((cell) => key(cell.row, cell.col)));
  }

  function getOrderedPathFromSelection(selection) {
    const cells = Array.from(selection).map((k) => {
      const [row, col] = k.split(",").map(Number);
      return { row, col };
    });

    if (cells.length === 0) return [];

    const sameRow = cells.every((c) => c.row === cells[0].row);
    const sameCol = cells.every((c) => c.col === cells[0].col);

    if (sameRow) {
      return cells.sort((a, b) => a.col - b.col);
    }

    if (sameCol) {
      return cells.sort((a, b) => a.row - b.row);
    }

    return [];
  }

  function getWordFromPath(path, g, activeBlock) {
    const letters = path.map(({ row, col }) => {
      if (activeBlock && row === activeBlock.row && col === activeBlock.col) {
        return activeBlock.letter;
      }
      return g[row][col] || "";
    });

    return {
      letters,
      word: letters.join(""),
      hasBlank: letters.some((l) => !l),
    };
  }

  const landingGhost = getLandingGhost();

  function phraseFrame(path) {
    if (!path?.length) return null;
    const rows = path.map((cell) => cell.row);
    const cols = path.map((cell) => cell.col);
    const minRow = Math.min(...rows);
    const minCol = Math.min(...cols);
    return {
      left: minCol * (cellSize + boardGap),
      top: minRow * (cellSize + boardGap),
      width: (Math.max(...cols) - minCol + 1) * (cellSize + boardGap) - boardGap,
      height: (Math.max(...rows) - minRow + 1) * (cellSize + boardGap) - boardGap,
    };
  }

  const selectionFrame = isDragging ? phraseFrame(dragPath) : null;
  const revealFrame = phraseFrame(selectionReveal);
  const revealVertical = selectionReveal?.length > 1 &&
    selectionReveal[0].col === selectionReveal[1].col;
  const revealReverse = selectionReveal?.length > 1 && (
    revealVertical
      ? selectionReveal[0].row > selectionReveal[selectionReveal.length - 1].row
      : selectionReveal[0].col > selectionReveal[selectionReveal.length - 1].col
  );

  const currentSelection = getWordFromPath(dragPath, grid, block).word;
  const invalidSelection = invalidHighlight.size > 0;
  const heldControls = (direction) => ({
    onPointerDown: (event) => {
      if (event.button !== 0) return;
      event.preventDefault();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      direction === "down" ? startSoftDrop() : startHorizontalMove(direction);
    },
    onPointerUp: () => direction === "down" ? stopSoftDrop() : stopHorizontalMove(direction),
    onPointerCancel: () => direction === "down" ? stopSoftDrop() : stopHorizontalMove(direction),
    onLostPointerCapture: () => direction === "down" ? stopSoftDrop() : stopHorizontalMove(direction),
    onClick: (event) => {
      if (event.detail !== 0) return;
      if (direction === "down") dropOneRow();
      else handleDirectionalInput(direction);
    },
  });
  const bonus = <div className={`bonus-mini ${showBuildBonusHit ? "bonus-hit" : ""}`}>
    <span>Build Bonus</span><b>{buildBonusLetter}</b><strong>2×</strong>
  </div>;
  const next = <div className="next-mini"><span>Next</span><b className={nextLetter === WILD ? "wild" : ""}>{nextLetter}</b></div>;

  return (
    <main ref={surfaceRef} className={`revelate-app screen-${screen}`} style={{ height: viewport.height }}>
      <header className="masthead">
        <RevelateLogo compact />
        <span className="masthead-caption">Plan. Build. Reveal. Score.</span>
        {screen === "game" ? (
          <button className="menu-button" onClick={goHome} disabled={clearing || showLevelUp} aria-label="Home and pause">HOME</button>
        ) : screen === "calculate" ? <button className="menu-button" onClick={() => setScreen("menu")}>HOME</button> : null}
      </header>

      {screen === "menu" && <section className="menu-surface">
        <RevelateLogo animated />
        <p className="brand-support">Plan. Build. Reveal. Score.</p>
        <RevelateDialogue />
        <div className="menu-stats">
          <div><span>Best score</span><b>{bestScore.toLocaleString()}</b></div>
          <div><span>Longest word</span><b>{bestLongestWord || "—"}</b></div>
        </div>
        <button className="primary-button" onClick={startGame}>START GAME <span aria-hidden="true">↗</span></button>
        <div className="menu-actions">
          <button onClick={saveGame} disabled={!savedGame}>{saveConfirmed ? "GAME SAVED" : "SAVE GAME"}</button>
          <button onClick={continueGame} disabled={!savedGame && !persistentSavedGame}>CONTINUE</button>
        </div>
        <div className="menu-bonus">{bonus}<p>Reveal words with <b>{buildBonusLetter}</b> for double points today.</p></div>
        <button className="calculate-entry" onClick={openCalculate}><span>TABULATE</span><small>Your words. Your score.</small><b aria-hidden="true">↗</b></button>
        <button className="text-button" onClick={() => setScreen("tips")}>How to play <span aria-hidden="true">↗</span></button>
      </section>}

      {screen === "calculate" && <CalculateDashboard game={reviewGame} games={reviewGames} anchor={reviewAnchor?.width === viewport.width && reviewAnchor?.height === viewport.height ? reviewAnchor : null} storageWarning={reviewStorageWarning} onPlay={startGame} />}

      {screen === "tips" && <VisualTutorial onHome={() => setScreen("menu")} />}

      {screen === "game" && <section className="game-layout" aria-label="Active game">
        <aside className="score-rail">
          <div className="score-block"><span className="eyebrow">Score</span><strong data-testid="score">{score.toLocaleString()}</strong><span className="best-score">Best <b>{bestScore.toLocaleString()}</b></span></div>
          <div className="level-block"><span className="eyebrow">Level <b>{String(level).padStart(2, "0")}</b></span><div className="milestones" aria-label={`${longWords} of ${LONG_WORD_TARGET} long words`}>{[0,1,2].map(i => <i key={i} className={longWords > i ? "complete" : ""}/>)}</div><span className="long-count">6+ letters <b>{longWords}/{LONG_WORD_TARGET}</b></span></div>
          <div className="rail-note"><span className="note-number">01 / BUILD</span><p>Let your<br/>words grow.</p><small>Completed words stay.<br/>You choose when to reveal.</small></div>
        </aside>

        <section className={`chamber ${wordReceipt ? "chamber-scored" : ""} ${hookImpact ? "chamber-hooked" : ""}`} aria-label="Letter chamber">
          <div className="chamber-top"><span className="board-state"><i/>{gameOver ? "ROUND COMPLETE" : showLevelUp ? "LEVEL UP" : "IN PLAY"}</span><div className="mobile-next">{next}</div><div className="mobile-bonus">{bonus}</div><span className="column-count">08 COLUMNS</span></div>
          <div className="board-stage" ref={stageRef}>
            <div ref={boardTouchRef} data-testid="game-board" className="game-board" role="application" aria-label="Revelate board, 8 columns and 11 rows. Drag across or down to reveal a word. Press R for keyboard selection." tabIndex={0}
              onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} onTouchCancel={cancelDragSelection}
              onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleTouchEnd} onMouseLeave={cancelDragSelection} onWheel={handleWheel}
              style={{ width: boardWidth, height: boardHeight, "--tile-size": `${cellSize}px`, "--tile-step": `${cellSize + boardGap}px` }}>
              <div className="tile-grid" style={{ gridTemplateColumns: `repeat(${COLS}, ${cellSize}px)`, gap: boardGap }}>
                {grid.map((row,r) => row.map((cell,c) => {
                  const active = isActiveBlock(r,c);
                  const ghost = landingGhost?.row === r && landingGhost?.col === c;
                  const lit = highlighted.has(key(r,c));
                  const selected = selectedCells.has(key(r,c));
                  const invalid = invalidHighlight.has(key(r,c));
                  const revealingLetter = lit && selectionReveal?.find(item => item.row === r && item.col === c)?.letter;
                  const hookLetter = lit && hookImpact?.row === r && hookImpact?.col === c ? block.letter : null;
                  const letter = revealingLetter || hookLetter || (active ? block.letter : ghost ? landingGhost.letter : cell || "");
                  const state = invalid ? "invalid" : lit ? "revealing" : selected ? "selected" : active ? "falling" : ghost ? "ghost" : cell ? "settled" : "empty";
                  return <div key={`${r}-${c}`} data-cell={`${r},${c}`} data-state={state} aria-label={letter ? `${letter}, row ${r+1}, column ${c+1}${active ? ", falling" : ghost ? ", landing preview" : ""}` : undefined}
                    className={`tile tile-${state} ${letter === WILD ? "tile-wild" : ""} ${letter === buildBonusLetter && (cell || active) ? "tile-bonus" : ""} ${keyboardMode && keyboardCursor.row === r && keyboardCursor.col === c ? "keyboard-cursor" : ""}`}
                    style={{ width: cellSize, height: cellSize, fontSize: letter.length > 1 ? multiTileFontSize : tileFontSize }}>
                    <span>{letter}</span>
                  </div>;
                }))}
              </div>
              {selectionFrame && <div className="selection-outline" data-testid="selection-outline" aria-hidden="true" style={{ ...selectionFrame, pointerEvents: "none" }}/ >}
              {revealFrame && <div data-testid="selection-reveal" className={`selection-reveal ${revealVertical ? "vertical" : ""}`} aria-hidden="true" style={revealFrame}><div className="revelate-reveal-sweep" style={{ animationDirection: revealReverse ? "reverse" : "normal" }}/></div>}
              {hookImpact && <div key={hookImpact.id} className={`hook-impact hook-${hookImpact.direction}`} data-testid="hook-impact" aria-hidden="true" style={{ left: hookImpact.col * (cellSize + boardGap), top: hookImpact.row * (cellSize + boardGap), width: cellSize, height: cellSize }}><span>{hookImpact.direction === "left" ? "↤" : "↦"}</span></div>}
              {showLevelUp && <div className="level-up" role="status"><small>ROOM TO BUILD</small><strong>Level {String(level).padStart(2,"0")}</strong></div>}
            </div>
          </div>
          <div className={`reveal-receipt ${wordReceipt ? "receipt-active" : ""} ${invalidSelection ? "receipt-invalid" : ""}`} aria-live="polite" aria-atomic="true">
            {wordReceipt ? <div key={wordReceipt.id} className="receipt-content" role="status"><span className="receipt-kind">{wordReceipt.kind === "hook" ? "↳ HOOKED" : "↗ REVEALED"}</span><strong>{wordReceipt.words}</strong><b>+{wordReceipt.points}</b>{showBuildBonusHit && <span className="receipt-bonus">2×</span>}</div>
              : invalidSelection ? <span>Not a word. Keep building.</span>
              : isDragging ? <div className="selection-readout"><strong>{currentSelection || "Select a word"}</strong><span>{keyboardMode ? "Enter to reveal" : "Release to reveal"}</span></div>
              : keyboardMode ? <span>Arrows: cursor · Space: anchor · Enter: reveal</span>
              : <span className="resting-receipt">BUILD AT YOUR PACE. REVEAL AT YOUR MOMENT.</span>}
          </div>
        </section>

        <aside className="strategy-rail">
          <div className="next-block"><span className="eyebrow">Up next</span><div className={`next-tile ${nextLetter === WILD ? "wild" : ""}`}>{nextLetter}</div></div>
          <div className={`bonus-card ${showBuildBonusHit ? "bonus-hit" : ""}`}><span className="eyebrow">Build Bonus</span><div><b>{buildBonusLetter}</b><strong>2×</strong></div><p>Words with <b>{buildBonusLetter}</b><br/>score double today.</p></div>
          <div className="rail-note"><span className="note-number">02 / HOOK</span><p>Catch the<br/>possibility.</p><small>Align beside a word.<br/>Press Hook before landing.</small></div>
        </aside>

        <div className="control-dock" aria-label="Tile controls">
          <button {...heldControls("left")} className={horizontalMoveHeld === "left" ? "control-held" : ""} aria-label="Move left"><span className="control-icon">←</span><span className="control-label">Left</span></button>
          <button {...heldControls("down")} className={softDropHeld ? "control-held" : ""} aria-label="Soft drop"><span className="control-icon">↓</span><span className="control-label">Drop</span></button>
          <button {...heldControls("right")} className={horizontalMoveHeld === "right" ? "control-held" : ""} aria-label="Move right"><span className="control-icon">→</span><span className="control-label">Right</span></button>
          <button className={`hook-button ${hookImpact ? "control-held" : ""}`} onClick={handleHook} aria-label="Hook falling tile"><span className="control-icon">↳</span><span>Hook</span><kbd>↑</kbd></button>
        </div>
        <div className="input-hint"><span>Drag a word to reveal</span><span className="keyboard-hint">Arrows / WASD to move · R to select</span></div>
      </section>}
    </main>
  );
}
